import { useParams, Link } from 'react-router-dom';
import { projects } from '../data/projects';
import { ui } from '../data/ui';
import { SEO } from '../components/ui/SEO';
import { Button } from '../components/ui/Button';
import { ScrollReveal } from '../components/ui/ScrollReveal';
import './ProjectDetailPage.css';

const isPlaceholder = (value: string) => !value || value.startsWith('[');

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const project = projects.find((p) => p.id === id);

  if (!project) {
    return (
      <div className="page-header">
        <div className="container" style={{ textAlign: 'center' }}>
          <h1>{ui.projects.notFound}</h1>
          <Button to="/projects" variant="primary">{ui.projects.backToProjects}</Button>
        </div>
      </div>
    );
  }

  const sections = [
    { title: ui.projects.overview, content: project.overview },
    { title: ui.projects.myRole, content: project.role },
    { title: ui.projects.problem, content: project.problem },
    { title: ui.projects.solution, content: project.solution },
    { title: ui.projects.architecture, content: project.architecture },
    { title: ui.projects.companyInfo, content: project.companyInfo },
  ].filter((s) => s.content);

  return (
    <>
      <SEO title={`${project.name} — ${ui.projects.overview}`} description={project.shortDescription} />

      <div className="project-detail">
        <div className="project-detail-hero">
          <div className="container">
            <Link to="/projects" className="back-link">→ {ui.projects.backToProjects}</Link>
            <ScrollReveal>
              <div className="project-detail-header">
                <div className="project-detail-meta">
                  <span className="tag">{project.type}</span>
                  {project.status && <span className="project-status">{project.status}</span>}
                </div>
                <h1>{project.name}</h1>
                {project.company && <p className="project-company">{project.company}</p>}
                <p className="project-detail-desc">{project.description}</p>
                <div className="project-detail-actions">
                  {project.playStoreUrl && (
                    <Button href={project.playStoreUrl} variant="primary">{ui.projects.googlePlay}</Button>
                  )}
                  {project.appStoreUrl && (
                    <Button href={project.appStoreUrl} variant="primary">{ui.projects.appStore}</Button>
                  )}
                  {project.projectUrl && !isPlaceholder(project.projectUrl) && (
                    <Button href={project.projectUrl} variant="secondary">{ui.projects.viewProject}</Button>
                  )}
                  {project.githubUrl && !isPlaceholder(project.githubUrl) && (
                    <Button href={project.githubUrl} variant="secondary">GitHub</Button>
                  )}
                  {project.caseStudyUrl && !isPlaceholder(project.caseStudyUrl) && (
                    <Button href={project.caseStudyUrl} variant="secondary">{ui.projects.caseStudy}</Button>
                  )}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>

        <div className="container project-detail-body">
          <ScrollReveal className="project-detail-image">
            {!isPlaceholder(project.projectImage) ? (
              <img src={project.projectImage} alt={project.name} loading="lazy" />
            ) : (
              <div className="placeholder-image project-detail-placeholder">
                {ui.projects.addScreenshot}
              </div>
            )}
          </ScrollReveal>

          <div className="project-detail-sections">
            {sections.map((section, i) => (
              <ScrollReveal key={section.title} delay={i * 0.05}>
                <div className="detail-section glass-card">
                  <h2>{section.title}</h2>
                  <p>{section.content}</p>
                </div>
              </ScrollReveal>
            ))}

            {project.keyFeatures && project.keyFeatures.length > 0 && (
              <ScrollReveal>
                <div className="detail-section glass-card">
                  <h2>{ui.projects.keyFeatures}</h2>
                  <ul className="feature-list">
                    {project.keyFeatures.map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            )}

            <ScrollReveal>
              <div className="detail-section glass-card">
                <h2>{ui.projects.technologies}</h2>
                <div className="tech-tags">
                  {project.technologies.map((tech) => (
                    <span key={tech} className="tag tag-secondary">{tech}</span>
                  ))}
                </div>
              </div>
            </ScrollReveal>

            {project.screenshots && project.screenshots.length > 0 && (
              <ScrollReveal>
                <div className="detail-section">
                  <h2>{ui.projects.screenshots}</h2>
                  <div className="screenshots-grid">
                    {project.screenshots.map((shot, i) => (
                      <div key={i} className="screenshot-item glass-card">
                        {!isPlaceholder(shot) ? (
                          <img src={shot} alt={`${project.name} ${i + 1}`} loading="lazy" />
                        ) : (
                          <div className="placeholder-image">{ui.projects.addScreenshotPlaceholder}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </ScrollReveal>
            )}

            {project.estimatedCost && !isPlaceholder(project.estimatedCost) && (
              <ScrollReveal>
                <div className="detail-section glass-card">
                  <h2>{ui.projects.estimatedCost}</h2>
                  <p>{project.estimatedCost}</p>
                </div>
              </ScrollReveal>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
